import { useEffect, useMemo, useRef, useState } from 'react';
import {
  labClient, liveClient, liveConfigured, avatarId,
  type AvatarClient, type Build, type Pack, type Take,
} from '../lib/avatarClient';
import { accessToken, getSession, onSessionChange } from '../lib/auth';
import { AuthSheet } from './AuthSheet';

/**
 * ?tab=avatar — "Dress rehearsal" (the owner's pick, onboarding option 3; mockup in
 * BestFriendNight qa/2d-avatars/onboarding/opt-3): you arrive at an empty seat in a small
 * theater with friends seated, take a selfie, the three sketches play on the screen like
 * trailers, you pick one, the avatar builds IN YOUR SEAT on the real clock (a build takes
 * minutes; what you see is what has really landed), the turnaround plays, you test your turn
 * with a LIVE mic level check (nothing is recorded or sent: no MediaRecorder, the analyser is
 * never connected to an output or a network), and you're set for Thursday.
 *
 * The outfit is whatever you wear in the photo (no presets). Live by default: the ThursdayNight
 * backend behind the site's sign-in; `&lab=1` replays a real build as a labelled time-lapse with no
 * upload; `&night=<eventId>` links the avatar to a night.
 */

type Phase = 'arrive' | 'sketching' | 'pick' | 'building' | 'turn' | 'test' | 'done';
const STEP: Record<Phase, number> = { arrive: 1, sketching: 2, pick: 3, building: 4, turn: 5, test: 6, done: 7 };
/** null = your seat; '' = an empty seat saved for a friend still to come. */
const FRIENDS = ['walt', 'gus', null, 'ben', ''] as const;
const FRIEND_NAME: Record<string, string> = { walt: 'Walt', gus: 'Gus', ben: 'Ben' };
const FART = '/assets/avatar-lab/friends';
const SPIN = ['closed', 'three_quarter_r', 'side_r', 'back', 'side_l', 'three_quarter_l'];
const TALK_MOUTHS = ['eh', 'oh', 'ah', 'oo', 'eh', 'closed'];
/** Every drawing a build makes, in plain words, for the "what just landed" line. */
const FRAME_WORDS: Record<string, string> = {
  welcome: 'a wave', handsup: 'hands up', pockets: 'hands in pockets', point: 'pointing', closed: 'your resting face',
  eye_blink: 'a blink', eye_browup: 'raised brows', eye_browone: 'one brow up', teeth: 'a grin', eh: 'mouth “eh”',
  ah: 'mouth “ah”', oh: 'mouth “oh”', oo: 'mouth “oo”', fv: 'mouth “f”', three_quarter_l: 'your ¾ turn',
  three_quarter_r: 'your ¾ turn', side_l: 'your profile', side_r: 'your profile', back: 'the back of your head',
};

export function AvatarMaker() {
  const q = new URLSearchParams(window.location.search);
  const night = q.get('night');
  // Opened from the web theater's "make yours" tile: where to send the finished avatar.
  // Only a theater page (http(s), path under /theater/), so this is no open redirect.
  const theater = (() => {
    try {
      const u = new URL(q.get('theater') ?? '');
      return /^https?:$/.test(u.protocol) && u.pathname.startsWith('/theater/') ? u : null;
    } catch { return null; }
  })();
  const wantLive = q.get('lab') !== '1' && liveConfigured();
  const [signedIn, setSignedIn] = useState(() => getSession() !== null);
  useEffect(() => onSessionChange((s) => setSignedIn(s !== null)), []);
  const client: AvatarClient | null = useMemo(
    () => (!wantLive ? labClient(Math.max(0.1, Number(q.get('speed') ?? 1) || 1)) : signedIn ? liveClient(accessToken, night) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signedIn],
  );

  const [phase, setPhase] = useState<Phase>('arrive');
  const [label, setLabel] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [takes, setTakes] = useState<Take[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [build, setBuild] = useState<Build | null>(null);
  const [buildAt, setBuildAt] = useState(0);
  const [pack, setPack] = useState<Pack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  const fileRef = useRef<HTMLInputElement>(null);
  const name = avatarId(label);

  if (wantLive && !signedIn) {
    // the magic link lands on the home page: App sends the guest back here after it signs them in
    try { localStorage.setItem('bfn_after_auth', window.location.search); } catch { /* private mode: they come back by hand */ }
    return <AuthSheet title="Sign in to make your avatar" />;
  }
  if (!client) return null;
  const lab = client.mode === 'lab';

  const run = async (fn: () => Promise<void>, back: Phase) => {
    setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setPhase(back); }
  };

  const sketch = (file: File) => run(async () => {
    setPhoto(file);
    setPhase('sketching');
    setTakes([]);
    setPicked(null);
    const seed = Math.floor(Math.random() * 1e6);
    await client.sketch({ photo: file, name, label: label.trim(), outfit: 'photo', seeds: [seed, seed + 7919, seed + 15838] },
      (t) => setTakes((prev) => [...prev, t]));
    setPhase('pick');
  }, 'arrive');

  const make = () => run(async () => {
    if (picked === null) return;
    setPhase('building');
    setBuild(null);
    setBuildAt(Date.now());
    setPack(await client.build({ name, label: label.trim(), take: picked }, setBuild));
    setPhase('turn');
  }, 'pick');

  return (
    <div className="dr">
      <div className="dr-head">
        <span className="dr-step">Step {STEP[phase]} / 7</span>
        {lab && <span className="chip" title="A real build replayed, sped up. Your photo stays on your device.">Lab replay · time-lapse ×{client.timelapse}</span>}
      </div>
      <Theater
        phase={phase} name={label.trim()} photoUrl={photoUrl} takes={takes} picked={picked} onPick={setPicked}
        build={build} buildAt={buildAt} pack={pack} lab={lab}
      />
      <div className="dr-sheet card">
        {error && <div className="notice error">{error}</div>}
        {phase === 'arrive' && (
          <>
            <h3 className="display">Pull up a seat</h3>
            <p>The glowing seat is yours. Walt, Gus and Ben are already here, and one seat is saved for a friend still on the way.</p>
            <label htmlFor="avatar-name">What should your friends call you?</label>
            <input id="avatar-name" type="text" maxLength={30} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Your name" />
            <p className="soft dr-small">You’ll be drawn in what you’re wearing in the selfie.</p>
            <input ref={fileRef} type="file" accept="image/*" capture="user" hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void sketch(f); }} />
            <button className="btn primary cta" disabled={!label.trim()} onClick={() => fileRef.current?.click()}>Take a selfie</button>
          </>
        )}
        {phase === 'sketching' && (
          <>
            <h3 className="display">Drawing three takes on you</h3>
            <p>{takes.length ? `${takes.length} of 3 on the big screen…` : 'Reading your photo…'}</p>
            <p className="soft dr-small">{lab ? 'Replay: the real sketches took about two minutes.' : 'Usually a minute or two.'}</p>
          </>
        )}
        {phase === 'pick' && (
          <>
            <h3 className="display">Pick your favorite</h3>
            <p>Three takes on you are showing on the big screen. Tap the one that’s you.</p>
            <div className="chip-grid" role="radiogroup" aria-label="Takes">
              {takes.map((t, i) => (
                <button key={t.seed} role="radio" aria-checked={picked === t.seed} className={`btn small ${picked === t.seed ? 'primary' : 'secondary'}`} onClick={() => setPicked(t.seed)}>
                  Take {i + 1}
                </button>
              ))}
            </div>
            <button className="btn primary cta" disabled={picked === null} onClick={make}>Make this one</button>
            <button className="btn secondary small" onClick={() => fileRef.current?.click()}>Retake the selfie</button>
            <input ref={fileRef} type="file" accept="image/*" capture="user" hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void sketch(f); }} />
          </>
        )}
        {phase === 'building' && <Building build={build} startedAt={buildAt} timelapse={client.timelapse ?? 1} />}
        {phase === 'turn' && pack && (
          <>
            <h3 className="display">Your turnaround</h3>
            <p>Front, three-quarter, side, back: this is how friends see you from their seats.</p>
            <button className="btn primary cta" onClick={() => setPhase('test')}>Test your turn</button>
          </>
        )}
        {phase === 'test' && pack && <TestTurn onDone={() => setPhase('done')} />}
        {phase === 'done' && (
          <div className="dr-done">
            <div className="dr-check">✓</div>
            <h3 className="display">You’re all set for Thursday</h3>
            <p className="soft">{label.trim()} · {lab ? 'a lab replay: nothing was uploaded or saved' : 'your avatar is saved for the theater'}</p>
            {theater && pack && !lab && (
              // <uid>/<slug>/v<n>: the pack's path in the public bfn-avatars bucket, which is
              // what the host downloads pack/v<n>/theater/<frame>.png from
              <a className="btn primary cta" href={`${theater.origin}${theater.pathname}${theater.search}#tnpack=${encodeURIComponent(pack.base.split('/bfn-avatars/')[1]?.replace('/pack/', '/') ?? '')}`}>Use it in the theater</a>
            )}
            {night && <a className="btn secondary small" href={`?e=${encodeURIComponent(night)}`}>Back to the night</a>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- the theater ---------------------------------------------------------------------------

function useNow(ms: number, on = true): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms, on]);
  return now;
}

/** A pack frame cropped to the manifest's figure slice (frames carry wide canvases for raised arms). */
function Fig({ pack, tag, className, flip }: { pack: Pack; tag: string; className?: string; flip?: boolean }) {
  const m = pack.manifest as { w?: number; figure?: { x: number; w: number } };
  const fw = m.figure?.w ?? m.w ?? 1;
  const style = { width: `${(100 * (m.w ?? fw)) / fw}%`, marginLeft: `${(-100 * (m.figure?.x ?? 0)) / fw}%` };
  return (
    <div className={`dr-fig ${className ?? ''}`} style={flip ? { transform: 'scaleX(-1)' } : undefined}>
      <img src={pack.frames[tag] ?? pack.frames.closed} alt="" style={style} />
    </div>
  );
}

interface TheaterProps {
  phase: Phase; name: string; photoUrl: string | null; takes: Take[]; picked: number | null; onPick: (s: number) => void;
  build: Build | null; buildAt: number; pack: Pack | null; lab: boolean;
}

function Theater(p: TheaterProps) {
  const { phase, pack } = p;
  const now = useNow(phase === 'turn' ? 1100 : 250, phase === 'turn' || phase === 'test' || phase === 'arrive' || phase === 'done');
  const talk = useTalk();   // set by TestTurn through a module-level store
  const dark = phase === 'test';
  const zoom = phase === 'building' || phase === 'turn';
  const blink = (i: number) => (now / 1000 + i * 1.1) % (3.4 + i * 0.45) < 0.18;
  const spinTag = SPIN[Math.floor(now / 1100) % SPIN.length];

  let you: JSX.Element;
  if (pack && (phase === 'turn' || phase === 'test' || phase === 'done')) {
    const tag = phase === 'turn' ? spinTag : phase === 'test' ? (talk.talking ? 'three_quarter_r' : 'back') : blink(2) ? 'eye_blink' : 'closed';
    you = <Fig pack={pack} tag={tag} key={tag} className="dr-flip" />;
  } else if (phase === 'building') {
    you = <SeatBuild build={p.build} take={p.takes.find((t) => t.seed === p.picked)?.url ?? null} />;
  } else {
    you = <div className="dr-empty"><span className="dr-you">YOU</span></div>;
  }

  return (
    <div className={`dr-theater${dark ? ' dark' : ''}`}>
      <div className="dr-floor" />
      <div className="dr-curtain l" /><div className="dr-curtain r" /><div className="dr-valance" />
      <div className="dr-screen"><Screen {...p} /></div>
      <div className={`dr-row${zoom ? ' zoom' : ''}`}>
        {FRIENDS.map((f, i) => (
          <div key={i} className={`dr-slot${f === null ? ' me' : ''}${f !== null && zoom ? ' dim' : ''}${f === null && talk.talking && dark ? ' talking' : ''}`}>
            {f === '' ? null : f ? <img className="dr-friend" alt={FRIEND_NAME[f]} style={f === 'ben' && dark && talk.talking ? { transform: 'scaleX(-1)' } : undefined}
              src={`${FART}/${f}_${dark ? (f === 'ben' && talk.talking ? 'threeq' : 'back') : blink(i) ? 'eye_blink' : 'closed'}.webp`} /> : you}
            <div className="dr-chair" />
            {f === null && (pack || phase === 'building') && p.name && <div className="dr-plate">{p.name}</div>}
          </div>
        ))}
      </div>
      {dark && talk.talking && pack && (
        <div className="dr-talkcard"><Fig pack={pack} tag={TALK_MOUTHS[Math.floor(now / 120) % TALK_MOUTHS.length]} /><b>{p.name || 'You'} · talking</b></div>
      )}
    </div>
  );
}

function Screen({ phase, photoUrl, takes, picked, onPick, build, pack, lab }: TheaterProps) {
  if (phase === 'sketching' || phase === 'pick') {
    if (!takes.length) {
      return <div className="dr-reading">{photoUrl && <img src={photoUrl} alt="Your photo" />}<b>READING YOUR PHOTO…</b></div>;
    }
    return (
      <div className="dr-trailers">
        <div className="dr-showing">NOW SHOWING · THREE TAKES ON YOU</div>
        <div className="dr-takes">
          {[0, 1, 2].map((i) => {
            const t = takes[i];
            return t ? (
              <button key={t.seed} className={`dr-take${picked === t.seed ? ' picked' : picked !== null ? ' other' : ''}`} onClick={() => onPick(t.seed)} aria-pressed={picked === t.seed} disabled={phase !== 'pick'}>
                <img src={t.url} alt={`Take ${i + 1}`} /><i>TAKE {i + 1}</i><u>COMING ATTRACTION</u>
              </button>
            ) : <div key={i} className="dr-take pending"><span className="spinner" /></div>;
          })}
        </div>
      </div>
    );
  }
  if (phase === 'building') {
    const frames = Object.entries(build?.frames ?? {});
    const last = frames[frames.length - 1];
    return last ? (
      <div className="dr-reading"><img src={last[1]} alt="" key={last[0]} className="dr-land" /><b>JUST DRAWN: {(FRAME_WORDS[last[0]] ?? last[0]).toUpperCase()}</b></div>
    ) : <div className="dr-idle small">{lab ? 'REPLAY · ' : ''}SETTING UP THE EASEL…</div>;
  }
  if (phase === 'test') return <div className="dr-movie"><span>THE GREAT TEST FEATURE</span></div>;
  if (phase === 'turn' && pack) return <div className="dr-idle small">YOUR TURNAROUND</div>;
  return <div className="dr-idle">THURSDAY NIGHT</div>;
}

/** Your seat while the real build runs: the picked take, inked in to the share of the build that has
 *  really landed (step / total), turning from pencil to colour. Nothing here runs ahead of the job. */
function SeatBuild({ build, take }: { build: Build | null; take: string | null }) {
  const f = build ? Math.min(1, build.step / Math.max(1, build.total)) : 0;
  if (!take) return <div className="dr-empty" />;
  return (
    <div className="dr-fig dr-sketch">
      <img src={take} alt="" style={{ clipPath: `inset(0 0 ${(1 - Math.max(0.08, f)) * 100}% 0)`, filter: `grayscale(${1 - f}) contrast(${1 + (1 - f)})` }} />
    </div>
  );
}

function Building({ build, startedAt, timelapse }: { build: Build | null; startedAt: number; timelapse: number }) {
  const lab = timelapse !== 1;
  const now = useNow(1000);
  const s = Math.max(0, Math.floor(((now - startedAt) / 1000) * timelapse));
  const frames = Object.keys(build?.frames ?? {});
  const pct = build ? Math.max(4, (100 * build.step) / Math.max(1, build.total)) : 4;
  return (
    <>
      <h3 className="display">Building you, in your seat</h3>
      <div className="mono dr-clock">{lab ? 'replayed build time' : 'build time'} {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')} · usually about 11 minutes</div>
      <div className="baking-bar-track"><div className="baking-bar-fill" style={{ width: `${pct}%` }} /></div>
      <p className="soft dr-small">
        {build?.stage === 'views' ? 'Turning you around for the seats beside you…' : build?.stage === 'publishing' ? 'Hanging your name on the seat…' : `${frames.length} drawings landed. Each one shows on the screen as it’s done.`}
        {!lab && ' You can keep this tab open; it carries on if you switch away.'}
      </p>
      <div className="dr-tiles">{frames.map((t) => <img key={t} src={build!.frames[t]} alt={FRAME_WORDS[t] ?? t} title={FRAME_WORDS[t] ?? t} />)}</div>
    </>
  );
}

// ---- test your turn: a live mic LEVEL, nothing recorded ----------------------------------------

type Talk = { level: number; talking: boolean };
let talkState: Talk = { level: 0, talking: false };
const talkSubs = new Set<(t: Talk) => void>();
function setTalk(t: Talk) { talkState = t; for (const f of talkSubs) f(t); }
function useTalk(): Talk {
  const [t, setT] = useState(talkState);
  useEffect(() => { talkSubs.add(setT); return () => { talkSubs.delete(setT); }; }, []);
  return t;
}

function TestTurn({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<'ask' | 'live' | 'blocked'>('ask');
  const [heard, setHeard] = useState(false);
  const talk = useTalk();
  const stop = useRef<() => void>(() => {});
  useEffect(() => () => { stop.current(); setTalk({ level: 0, talking: false }); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(an);   // analyser only: never a destination, never a recorder
      const buf = new Float32Array(an.fftSize);
      let raf = 0, lastLoud = 0, smooth = 0;
      const tick = () => {
        an.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        smooth = Math.max(Math.min(1, Math.sqrt(sum / buf.length) * 6), smooth * 0.85);
        if (smooth > 0.12) lastLoud = performance.now();
        const talking = performance.now() - lastLoud < 450;
        if (talking) setHeard(true);
        setTalk({ level: smooth, talking });
        raf = requestAnimationFrame(tick);
      };
      tick();
      stop.current = () => { stop.current = () => {}; cancelAnimationFrame(raf); stream.getTracks().forEach((t) => t.stop()); void ctx.close(); };
      setState('live');
    } catch {
      setState('blocked');
    }
  };

  if (state === 'ask') {
    return (
      <>
        <h3 className="display">Test your turn</h3>
        <p>The lights go down and the film starts. Say hi to the row and watch your seat.</p>
        <p className="soft dr-small">A live mic check only: nothing is recorded or sent. On Thursday, friends hear your real voice in the room.</p>
        <button className="btn primary cta" onClick={start}>Use my mic</button>
        <button className="btn secondary small" onClick={onDone}>Skip</button>
      </>
    );
  }
  if (state === 'blocked') {
    return (
      <>
        <h3 className="display">Test your turn</h3>
        <p>No mic allowed, so no test this time. Your avatar still turns when you talk on Thursday.</p>
        <button className="btn primary cta" onClick={onDone}>Done</button>
      </>
    );
  }
  const bars = 18;
  return (
    <>
      <h3 className="display">Test your turn</h3>
      <p>{talk.talking ? 'Hearing you: you turn to the row, and Ben turns to look.' : heard ? 'Back to the film. Say something else, or finish up.' : 'Say hi to the row.'}</p>
      <div className="dr-meter" aria-label="Mic level">{Array.from({ length: bars }, (_, i) => <i key={i} className={i / bars < talk.level ? (i > 12 ? 'hi' : 'on') : ''} />)}</div>
      <span className="dr-nr">● Live mic check · nothing is recorded</span>
      <button className="btn primary cta" onClick={() => { stop.current(); onDone(); }}>{heard ? 'Sounds good' : 'Finish'}</button>
    </>
  );
}
